<template>
  <footer class="fixed bottom-0 left-0 right-0 z-50">
    <div class="container mx-auto px-4">
      <div class="flex items-center justify-between h-12">
        <div class="flex items-center space-x-4">
          <div class="flex items-center space-x-2">
            <span class="text-xs ml-1"
                  :class="isOnline ? 'text-green-400' : 'text-red-400'">
              {{ isOnline ? 'ONLINE' : 'OFFLINE' }}
            </span>

            <div class="w-2 h-2 rounded-full animate-pulse"
                 :class="isOnline ? 'bg-green-400' : 'bg-red-500'"></div>

            <span class="text-[9px] text-gray-300 mr-1">: network</span>

            <span class="text-[9px] text-gray-300 mr-1"> | </span>
            <span
                @click="goCyber"
                class="cyber-text font-bold text-xs cursor-pointer select-none mr-1 inline-block "
            >
              -CYBERNITH-
            </span>
            <span class="text-[9px] text-gray-300">: encrypted by</span>
          </div>
        </div>

        <div class="flex flex-col items-center">
          <div class="flex items-center space-x-2 cursor-pointer group" @click="toggleDateTimeDropdown">
            <div class="flex items-center space-x-3">
              <span class="text-gray-300 text-sm font-light tracking-wider group-hover:text-white transition-colors">
                {{ systemBarDate }}
              </span>
              <span class="text-white text-base font-medium tracking-wider mr-1 group-hover:text-blue-200 transition-colors">
                {{ systemBarTime }}
              </span>
              <svg class="w-4 h-4 text-blue-400 group-hover:text-blue-300 transition-colors"
                   fill="none"
                   stroke="currentColor"
                   viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>

            </div>
          </div>
        </div>

        <div class="flex-shrink-0 items-center space-x-3">
          <button
              class="text-xs text-gray-400 hover:text-white transition-colors duration-200 focus:outline-none group"
              @click="toggleFullScreen"
              title="Toggle Fullscreen"
          >
            <div class="flex flex-col items-center">
              <svg class="w-5 h-5 mb-1 group-hover:scale-110 transition-transform"
                   fill="none"
                   stroke="currentColor"
                   viewBox="0 0 24 24">
                <path stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"/>
              </svg>
            </div>
          </button>
        </div>
      </div>

      <transition name="dropdown">
        <div v-if="showDateTimeDropdown"
             class="absolute left-1/2 transform -translate-x-1/2 top-full mt-1 w-72 z-50">
          <div class="liquid-glass-dropdown rounded-xl overflow-hidden border border-gray-700/50 shadow-2xl">
            <div class="bg-gradient-to-r from-blue-900/30 to-purple-900/30 p-4 border-b border-gray-700/50">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span class="text-white font-semibold mr-2">تاریخ و ساعت</span>
                </div>
                <button @click="showDateTimeDropdown = false" class="text-gray-400 hover:text-white transition-colors">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div class="p-6">
              <div class="text-center mb-6">
                <div class="text-5xl text-center font-bold text-white tracking-wider mb-2">
                  {{ systemBarTime }}
                </div>
                <div class="text-sm text-center text-white">{{ getDayName() }}</div>
              </div>

              <div class="bg-gray-900/50 rounded-lg p-4 mb-4">
                <div class="flex items-center justify-center space-x-3">
                  <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span class="text-2xl mr-2 font-bold text-white">{{ systemBarDate }}</span>
                </div>
              </div>
              <div class="bg-gray-900/50 rounded-lg p-4 mb-4">
                <div class="flex items-center justify-center space-x-3">
                  <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span class="text-2xl mr-2 font-bold text-white">{{ getGregorianDate() }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </footer>
</template>

<script>
import moment from "moment-jalaali";

export default {
  name: "SystemFooter",
  data() {
    return {
      systemBarDate: "",
      systemBarTime: "",
      isOnline: navigator.onLine,
      showDateTimeDropdown: false,
      dayNames: {
        0: "Sunday",
        1: "Monday",
        2: "Tuesday",
        3: "Wednesday",
        4: "Thursday",
        5: "Friday",
        6: "Saturday"
      }
    };
  },
  mounted() {
    this.updateTime();
    // Update every minute
    setInterval(() => {
      this.updateTime();
    }, 1000 * 60);

    // Update seconds for first minute
    const secondUpdater = setInterval(() => {
      this.updateTime();
    }, 1000);

    setTimeout(() => {
      clearInterval(secondUpdater);
    }, 1000 * 60);

    window.addEventListener("online", () => this.isOnline = true);
    window.addEventListener("offline", () => this.isOnline = false);

    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    goCyber() {
      window.open("https://sorooshmorshedi.ir", "_blank");
    },
    updateTime() {
      const now = moment();
      this.systemBarDate = now.format("jYYYY/jMM/jDD");
      this.systemBarTime = now.format("HH:mm");
    },
    toggleFullScreen() {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(err => {
          console.log(`Error attempting to enable fullscreen: ${err.message}`);
        });
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        }
      }
    },
    toggleDateTimeDropdown() {
      this.showDateTimeDropdown = !this.showDateTimeDropdown;
    },
    getDayName() {
      const dayIndex = moment().day();
      return this.dayNames[dayIndex] || "";
    },
    getGregorianDate() {
      return moment().format("YYYY-MM-DD");
    },
    handleClickOutside(event) {
      const dropdown = this.$el.querySelector('.liquid-glass-dropdown');
      const trigger = this.$el.querySelector('.cursor-pointer.group');

      if (this.showDateTimeDropdown && dropdown && trigger &&
          !dropdown.contains(event.target) && !trigger.contains(event.target)) {
        this.showDateTimeDropdown = false;
      }
    }
  }
};
</script>

<style scoped>
footer {
  background: linear-gradient(90deg,
  rgba(15, 23, 42, 0.98) 0%,
  rgba(30, 41, 59, 0.98) 50%,
  rgba(15, 23, 42, 0.98) 100%
  );
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  height: 48px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 -4px 25px rgba(0, 0, 0, 0.4);
}

.liquid-glass-dropdown {
  background: linear-gradient(135deg,
  rgba(255, 255, 255, 0.35) 0%,
  rgba(255, 255, 255, 0.2) 100%
  );
  backdrop-filter: blur(120px) saturate(180%);
  -webkit-backdrop-filter: blur(120px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow:
      0 20px 60px rgba(0, 0, 0, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.15),
      0 0 0 1px rgba(255, 255, 255, 0.05);
  position: relative;
  overflow: hidden;
}

/* برای مات شدن بیشتر لبه‌ها، overlay اضافه کنید */
.liquid-glass-dropdown::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.2);  /* overlay مات آبی تیره */
  backdrop-filter: blur(10px);
  z-index: -1;
  pointer-events: none;
}

/* شفافیت را کمی کاهش دهید تا مات‌تر شود */
.liquid-glass-dropdown > * {
  position: relative;
  z-index: 1;
}

.liquid-glass-dropdown::after {
  content: '';
  position: absolute;
  top: -100%;
  left: -100%;
  width: 300%;
  height: 300%;
  background: radial-gradient(
      circle at center,
      rgba(59, 130, 246, 0.15) 0%,
      rgba(30, 41, 59, 0.3) 40%,
      transparent 80%
  );
  animation: liquid-shimmer 10s ease-in-out infinite;
  pointer-events: none;
  filter: blur(50px);
}

.dropdown-enter-active {
  animation: dropdown-slide 0.3s ease-out;
}

.dropdown-leave-active {
  animation: dropdown-slide 0.2s ease-in reverse;
}

@keyframes dropdown-slide {
  0% {
    opacity: 0;
    transform: translate(-50%, -20px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, 0) scale(1);
  }
}

@keyframes liquid-shimmer {
  0%, 100% {
    transform: translate(0, 0) rotate(0deg);
  }
  25% {
    transform: translate(10px, 10px) rotate(2deg);
  }
  50% {
    transform: translate(0, 20px) rotate(0deg);
  }
  75% {
    transform: translate(-10px, 10px) rotate(-2deg);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes cyber-text-rainbow {
  0% {
    color: #ff005e;
  }
  14% {
    color: #ff9100;
  }
  28% {
    color: #ffea00;
  }
  42% {
    color: #2bff00;
  }
  57% {
    color: #00fff7;
  }
  71% {
    color: #0077ff;
  }
  85% {
    color: #b300ff;
  }
  100% {
    color: #ff005e;
  }
}

.cyber-text {
  animation: cyber-text-rainbow 3s linear infinite;
}

* {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>